import React, { useEffect, useState } from 'react';
import { Activity, Clock, Zap, AlertTriangle } from 'lucide-react';

const PerformanceMonitor = () => {
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    memoryUsage: 0,
    apiResponseTime: 0,
    errors: 0
  });

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Track page load time
    const loadTime = performance.now();
    setMetrics(prev => ({ ...prev, loadTime }));

    // Monitor memory usage
    if ('memory' in performance) {
      const updateMemoryUsage = () => {
        const memory = performance.memory;
        const usageMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
        setMetrics(prev => ({ ...prev, memoryUsage: usageMB }));
      };
      
      updateMemoryUsage();
      const interval = setInterval(updateMemoryUsage, 5000);
      return () => clearInterval(interval);
    }

    // Monitor API response times
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const start = performance.now();
      try {
        const response = await originalFetch(...args);
        const end = performance.now();
        const responseTime = end - start;
        
        setMetrics(prev => ({ 
          ...prev, 
          apiResponseTime: Math.round(responseTime)
        }));
        
        return response;
      } catch (error) {
        setMetrics(prev => ({ ...prev, errors: prev.errors + 1 }));
        throw error;
      }
    };

    // Monitor unhandled errors
    const handleError = (event) => {
      setMetrics(prev => ({ ...prev, errors: prev.errors + 1 }));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
      window.fetch = originalFetch;
    };
  }, []);

  // Show monitor in development or when there are issues
  useEffect(() => {
    const shouldShow = process.env.NODE_ENV === 'development' || 
                      metrics.errors > 0 || 
                      metrics.memoryUsage > 100;
    setIsVisible(shouldShow);
  }, [metrics]);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">Performance Monitor</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ×
        </button>
      </div>
      
      <div className="space-y-2 text-xs">
        <div className="flex items-center space-x-2">
          <Clock className="w-3 h-3 text-blue-500" />
          <span>Load: {Math.round(metrics.loadTime)}ms</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Activity className="w-3 h-3 text-green-500" />
          <span>Memory: {metrics.memoryUsage}MB</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Zap className="w-3 h-3 text-yellow-500" />
          <span>API: {metrics.apiResponseTime}ms</span>
        </div>
        
        {metrics.errors > 0 && (
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-3 h-3 text-red-500" />
            <span>Errors: {metrics.errors}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceMonitor; 